/* ===================================================================
   GROUP BUY POPUP OVERLAY — Tassenexpress
   - Magnetic bar (continuous, snaps softly to tier dots)
   - Allows any quantity 1–9999 (bar maxes at last tier visually)
   - Escalating discount-pill rewards + tier-reach celebrations
   - Replaces popupInitApp + popupTreateApp from custom.js
   =================================================================== */
(function () {
  'use strict';

  let didInit = false;
  document.addEventListener('DOMContentLoaded', init);
  if (document.readyState !== 'loading') init();

  function init() {
    if (didInit) return;
    didInit = true;

    const overlay = document.getElementById('pdp-group-buy-popup-overlay');
    if (!overlay) return;

    // === Read tier data from DOM ===
    const tierRowEls = Array.from(overlay.querySelectorAll('.gbpo-quantity'));
    if (tierRowEls.length === 0) return;

    const TIERS = tierRowEls.map((row, i) => ({
      qty:      parseInt(row.dataset.qty, 10),
      unit:     parseFloat(row.dataset.unit),
      priceRaw: (row.dataset.priceRaw || '').trim(),
      el:       row,
      idx:      i,
    })).filter(t => !isNaN(t.qty) && !isNaN(t.unit));
    if (TIERS.length === 0) return;

    const BASE     = TIERS[0].unit;
    const SEG      = Math.max(1, TIERS.length - 1);
    const MIN_QTY  = 1;
    const MAX_QTY  = 9999;
    const SNAP_PCT = 1.2;

    // Respect the OS "reduce motion" accessibility setting (no confetti / no count-up)
    function reduceMotion() {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }

    // === Discount badges come straight from the metafield (rendered in Liquid) ===
    // No JS override: keeps the merchant-authored "discount" text 1:1 like the
    // original popup. (BASE below is still used for the savings calculation.)

    // === Format helpers (German "," decimals) ===
    function fmt(n) {
      return n.toFixed(2).replace('.', ',') + '€';
    }
    function fmtShort(n) {
      let s = n.toFixed(2).replace(/0+$/, '').replace(/\.$/, '').replace('.', ',');
      return s + '€';
    }

    // === Tier math ===
    function tierIndex(qty) {
      let i = 0;
      for (let k = 0; k < TIERS.length; k++) if (qty >= TIERS[k].qty) i = k;
      return i;
    }
    function unitFor(qty)  { return TIERS[tierIndex(qty)].unit; }
    function nextTier(qty) { for (const t of TIERS) if (t.qty > qty) return t; return null; }

    function pctToQty(pct) {
      pct = Math.max(0, Math.min(100, pct));
      const s = (pct / 100) * SEG;
      const i = Math.min(Math.floor(s), SEG - 1);
      return Math.round(TIERS[i].qty + (TIERS[i + 1].qty - TIERS[i].qty) * (s - i));
    }
    function qtyToPct(qty) {
      if (qty <= TIERS[0].qty)   return 0;
      if (qty >= TIERS[SEG].qty) return 100;
      for (let i = 0; i < SEG; i++) {
        if (qty >= TIERS[i].qty && qty <= TIERS[i + 1].qty) {
          const f = (qty - TIERS[i].qty) / (TIERS[i + 1].qty - TIERS[i].qty);
          return ((i + f) / SEG) * 100;
        }
      }
      return 0;
    }

    // === Persistent FX canvas for celebrations ===
    const bFx = (() => {
      const canvas = document.createElement('canvas');
      canvas.className = 'gbpo-fx-canvas';
      document.body.appendChild(canvas);
      const ctx = canvas.getContext('2d');
      let dpr = window.devicePixelRatio || 1;
      function resize() {
        dpr = window.devicePixelRatio || 1;
        canvas.width  = window.innerWidth  * dpr;
        canvas.height = window.innerHeight * dpr;
        canvas.style.width  = window.innerWidth  + 'px';
        canvas.style.height = window.innerHeight + 'px';
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(dpr, dpr);
      }
      resize();
      window.addEventListener('resize', resize);

      function drawStar(spikes, outerR, innerR) {
        let rot = Math.PI / 2 * 3;
        const step = Math.PI / spikes;
        ctx.beginPath();
        ctx.moveTo(0, -outerR);
        for (let i = 0; i < spikes; i++) {
          ctx.lineTo(Math.cos(rot) * outerR, Math.sin(rot) * outerR); rot += step;
          ctx.lineTo(Math.cos(rot) * innerR, Math.sin(rot) * innerR); rot += step;
        }
        ctx.closePath(); ctx.fill();
      }
      function drawSparkle(size) {
        const r = size / 2, inner = r * 0.22;
        ctx.beginPath();
        ctx.moveTo(0, -r); ctx.lineTo(inner, -inner); ctx.lineTo(r, 0);
        ctx.lineTo(inner, inner); ctx.lineTo(0, r); ctx.lineTo(-inner, inner);
        ctx.lineTo(-r, 0); ctx.lineTo(-inner, -inner);
        ctx.closePath(); ctx.fill();
      }

      const items = [];
      let running = false;
      function loop() {
        ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
        for (let i = items.length - 1; i >= 0; i--) {
          const it = items[i];
          it.x += it.vx; it.y += it.vy;
          it.vy += it.gravity; it.vx *= it.drag;
          it.rot += it.rs; it.life -= it.fade;
          if (it.life <= 0 || it.y > window.innerHeight + 60) { items.splice(i, 1); continue; }
          ctx.save();
          ctx.translate(it.x, it.y);
          ctx.rotate(it.rot);
          ctx.globalAlpha = Math.min(1, it.life);
          ctx.fillStyle = it.color;
          if      (it.shape === 'circle')  { ctx.beginPath(); ctx.arc(0, 0, it.size / 2, 0, Math.PI * 2); ctx.fill(); }
          else if (it.shape === 'star')    { drawStar(5, it.size / 2, it.size * 0.21); }
          else if (it.shape === 'sparkle') { drawSparkle(it.size); }
          else                              { ctx.fillRect(-it.size / 2, -it.size / 3, it.size, (it.size / 3) * 2); }
          ctx.restore();
        }
        if (items.length > 0) requestAnimationFrame(loop);
        else { running = false; ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr); }
      }
      function spawn(particles) {
        for (const p of particles) items.push(p);
        if (!running) { running = true; requestAnimationFrame(loop); }
      }
      return { spawn };
    })();

    const FX_COLORS = ['#173586','#2c5aff','#5a7dff','#ff6b35','#ff9d5c','#fbbf24','#16a34a','#ec4899'];

    function celebrate(el, tierIdx) {
      if (reduceMotion()) return;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top  + rect.height / 2;
      const intensity = tierIdx;
      const count   = 14 + intensity * 18;
      const speed   = 4  + intensity * 1.2;
      const upBias  = 2  + intensity * 1.3;
      const hasSparkles = intensity >= 2;
      const hasStars    = intensity >= 3;
      const hasFlash    = intensity >= 3;

      const particles = [];
      for (let i = 0; i < count; i++) {
        const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.95;
        const v = speed * (0.65 + Math.random() * 0.7);
        const r = Math.random();
        let shape;
        if      (hasStars && r < 0.20)    shape = 'star';
        else if (hasSparkles && r < 0.40) shape = 'sparkle';
        else if (r < 0.70)                shape = 'circle';
        else                              shape = 'rect';
        const isBig = (shape === 'star' || shape === 'sparkle');
        particles.push({
          x: cx + (Math.random() - 0.5) * 18,
          y: cy + (Math.random() - 0.5) * 8,
          vx: Math.cos(angle) * v + (Math.random() - 0.5) * 1.5,
          vy: Math.sin(angle) * v - upBias,
          gravity: 0.14 + Math.random() * 0.08,
          drag: 0.992,
          rot: Math.random() * Math.PI * 2,
          rs: (Math.random() - 0.5) * 0.35,
          size: isBig ? (hasStars ? 14 + Math.random() * 14 : 10 + Math.random() * 6) : 4 + Math.random() * 4,
          color: FX_COLORS[Math.floor(Math.random() * FX_COLORS.length)],
          shape: shape,
          life: 1.0 + Math.random() * 0.4,
          fade: 0.011 + Math.random() * 0.005,
        });
      }
      bFx.spawn(particles);

      el.classList.remove('burst');
      void el.offsetWidth;
      el.classList.add('burst');
      setTimeout(() => el.classList.remove('burst'), 600);

      const ring = document.createElement('div');
      ring.className = 'gbpo-fx-ring';
      ring.style.left = cx + 'px';
      ring.style.top  = cy + 'px';
      ring.style.borderColor = intensity >= 3 ? '#ff6b35' : '#173586';
      document.body.appendChild(ring);
      setTimeout(() => ring.remove(), 1000);

      if (hasFlash) {
        const flash = document.createElement('div');
        flash.className = 'gbpo-fx-flash';
        flash.style.background = intensity >= 4
          ? 'radial-gradient(circle at ' + cx + 'px ' + cy + 'px, rgba(255,107,53,0.22) 0%, rgba(23,53,134,0.08) 30%, transparent 65%)'
          : 'radial-gradient(circle at ' + cx + 'px ' + cy + 'px, rgba(23,53,134,0.18) 0%, transparent 55%)';
        document.body.appendChild(flash);
        setTimeout(() => flash.remove(), 750);
      }
    }

    // === DOM references ===
    const barWrap     = overlay.querySelector('.gbpo-bar-wrap');
    const fillEl      = overlay.querySelector('.gbpo-fill');
    const thumb       = overlay.querySelector('.gbpo-thumb');
    const dotEls      = Array.from(overlay.querySelectorAll('.gbpo-dot'));
    const qtyDisplay  = overlay.querySelector('.gbpo-quantity-selector-value');
    const minusBtn    = overlay.querySelector('.gbpo-quantity-selector-btn-minus');
    const plusBtn     = overlay.querySelector('.gbpo-quantity-selector-btn-plus');
    const customInput = overlay.querySelector('#gbpo-input');
    const customWrap  = overlay.querySelector('.gbpo-input-wrap');
    const subtotalEl  = overlay.querySelector('.gppo-total-price-value-container');
    const saveAmount  = overlay.querySelector('.gbpo-quantity-selector-discount-price');
    const saveBanner  = overlay.querySelector('.gbpo-quantity-selector-note');
    const nextQtyEl   = overlay.querySelector('.gbpo-limited-quantity');
    const nextPriceEl = overlay.querySelector('.gbpo-limited-quantity-result-price');
    const nextBanner  = overlay.querySelector('.gbpo-limited-quantity-offer');
    const ctaBtn      = overlay.querySelector('.gbpo-button');
    const closeBtn    = overlay.querySelector('.gbpo-close');
    const maskEl      = overlay.querySelector('.mask.close');

    // Main-PDP elements the ORIGINAL popup kept in sync (live OUTSIDE the overlay)
    const piQtyText   = document.querySelector('.pi-quantity-selector-text');
    const piSubtotal  = document.querySelector('.pi-subtotal-value');
    const piUnitPrice = document.querySelector('.pi-unit-price-value');

    // === Smooth count-up for savings ===
    let saveAnimRAF = 0;
    function animateSaving(target) {
      if (saveAnimRAF) cancelAnimationFrame(saveAnimRAF);
      if (reduceMotion()) {
        saveAmount.textContent = fmt(target);
        saveAmount.dataset.value = target;
        return;
      }
      const startVal = parseFloat(saveAmount.dataset.value);
      const from = isNaN(startVal) ? target : startVal;
      if (Math.abs(target - from) < 0.01) {
        saveAmount.textContent = fmt(target);
        saveAmount.dataset.value = target;
        return;
      }
      const t0 = performance.now();
      const dur = 320;
      function tick(now) {
        const t = Math.min(1, (now - t0) / dur);
        const eased = 1 - Math.pow(1 - t, 3);
        const v = from + (target - from) * eased;
        saveAmount.textContent = fmt(v);
        if (t < 1) saveAnimRAF = requestAnimationFrame(tick);
        else {
          saveAmount.textContent = fmt(target);
          saveAmount.dataset.value = target;
          saveAnimRAF = 0;
        }
      }
      saveAnimRAF = requestAnimationFrame(tick);
    }

    let currentQty   = TIERS[0].qty;
    let lastTierIdx  = 0;
    const MAX_TIER_QTY = TIERS[TIERS.length - 1].qty;

    function setQty(qty) {
      qty = Math.max(MIN_QTY, Math.min(MAX_QTY, Math.round(qty)));
      currentQty = qty;
      const pct  = qtyToPct(qty);
      const tIdx = tierIndex(qty);
      const unit = unitFor(qty);
      const total    = qty * unit;
      const save     = qty * BASE - total;
      const nxt      = nextTier(qty);
      const isAboveMaxTier = qty > MAX_TIER_QTY;

      fillEl.style.width = pct + '%';
      thumb.style.left   = pct + '%';
      thumb.setAttribute('aria-valuenow', qty);
      qtyDisplay.textContent = qty;

      dotEls.forEach((d, i) => d.classList.toggle('passed', i <= tIdx));

      tierRowEls.forEach((r, i) => r.classList.toggle('active', !isAboveMaxTier && i === tIdx));
      if (customWrap) customWrap.classList.toggle('active', isAboveMaxTier);

      if (subtotalEl) subtotalEl.textContent = fmt(total);

      // Keep the main PDP in sync — parity with the original popupTreateApp
      // (same string format the live code wrote: dot decimals, "ab" prefix, "p.S").
      if (piQtyText)   piQtyText.innerHTML   = qty;
      if (piSubtotal)  piSubtotal.innerHTML  = (qty > 1) ? total.toFixed(2) + '€' : 'ab ' + total.toFixed(2) + '€';
      if (piUnitPrice) piUnitPrice.innerHTML = 'ab ' + (TIERS[tIdx].priceRaw || unit) + '€ p.S';

      // Savings banner
      const willShow = save > 0.01;
      const wasShown = saveBanner && saveBanner.style.display !== 'none';
      if (saveBanner) saveBanner.style.display = willShow ? '' : 'none';
      if (willShow) {
        if (!wasShown) {
          saveAmount.dataset.value = 0;
          saveAmount.textContent = fmt(0);
        }
        animateSaving(save);
      }

      // Next-tier banner
      if (nxt) {
        if (nextQtyEl)   nextQtyEl.textContent   = nxt.qty - qty;
        if (nextPriceEl) nextPriceEl.textContent = fmtShort(nxt.unit);
        if (nextBanner)  nextBanner.style.display = '';
      } else if (nextBanner) {
        nextBanner.style.display = 'none';
      }

      // Custom input sync (skip while user is typing)
      if (customInput && document.activeElement !== customInput) {
        customInput.value = qty;
      }

      // Tier-up celebration
      if (tIdx > lastTierIdx) {
        celebrate(dotEls[tIdx], tIdx);
        if (saveAmount) {
          saveAmount.classList.remove('pulse');
          void saveAmount.offsetWidth;
          saveAmount.classList.add('pulse');
        }
        const reachedPill = tierRowEls[tIdx]?.querySelector('.gbpo-quantity-badge');
        if (reachedPill) {
          reachedPill.classList.remove('celebrate');
          void reachedPill.offsetWidth;
          reachedPill.classList.add('celebrate');
          setTimeout(() => reachedPill.classList.remove('celebrate'), 650);
        }
      }
      lastTierIdx = tIdx;
    }

    // === Magnetic drag ===
    function magnetSnapPct(pct) {
      for (let i = 0; i < TIERS.length; i++) {
        const tp = (i / SEG) * 100;
        if (Math.abs(pct - tp) <= SNAP_PCT) return tp;
      }
      return pct;
    }
    let dragging = false;
    function getX(e) { return e.touches ? e.touches[0].clientX : e.clientX; }
    function startDrag(e) {
      dragging = true;
      thumb.classList.add('dragging');
      moveDrag(e);
      e.preventDefault();
    }
    function moveDrag(e) {
      if (!dragging) return;
      const rect = barWrap.getBoundingClientRect();
      const x = getX(e) - rect.left;
      const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
      setQty(pctToQty(magnetSnapPct(pct)));
    }
    function endDrag() {
      if (!dragging) return;
      dragging = false;
      thumb.classList.remove('dragging');
    }
    if (barWrap) {
      barWrap.addEventListener('mousedown',  startDrag);
      barWrap.addEventListener('touchstart', startDrag, { passive: false });
    }
    window.addEventListener('mousemove', moveDrag);
    window.addEventListener('touchmove', moveDrag, { passive: false });
    window.addEventListener('mouseup',   endDrag);
    window.addEventListener('touchend',  endDrag);

    // Position tier dots evenly along the bar (one per tier, matches thumb math)
    dotEls.forEach((d, i) => { d.style.left = (i / SEG) * 100 + '%'; });

    // Click directly on tier dots
    dotEls.forEach(d => {
      d.addEventListener('click', (e) => {
        e.stopPropagation();
        const qty = parseInt(d.dataset.qty, 10);
        if (!isNaN(qty)) setQty(qty);
      });
    });

    // Click on tier rows in the list
    tierRowEls.forEach(row => {
      row.addEventListener('click', () => {
        const qty = parseInt(row.dataset.qty, 10);
        if (!isNaN(qty)) setQty(qty);
      });
    });

    // === ± buttons: single tap = one step, hold = auto-repeat (works on mouse AND touch) ===
    function attachStepper(btn, delta) {
      if (!btn) return;
      let timer, interval;
      const step  = () => setQty(currentQty + delta);
      const start = (e) => {
        e.preventDefault();          // also suppresses the synthetic click on touch -> no double step
        step();                      // immediate single step on press/tap (this is the mobile fix)
        timer = setTimeout(() => { interval = setInterval(step, 60); }, 380);  // then auto-repeat while held
      };
      const stop  = () => { clearTimeout(timer); clearInterval(interval); };
      btn.addEventListener('mousedown',  start);
      btn.addEventListener('touchstart', start, { passive: false });
      ['mouseup','mouseleave','touchend','touchcancel'].forEach(ev => btn.addEventListener(ev, stop));
    }
    attachStepper(minusBtn, -1);
    attachStepper(plusBtn,  +1);

    // === Custom input ===
    if (customInput) {
      customInput.addEventListener('input', () => {
        const v = parseInt(customInput.value, 10);
        if (!isNaN(v) && v >= MIN_QTY && v <= MAX_QTY) setQty(v);
      });
      customInput.addEventListener('blur',    () => { customInput.value = currentQty; });
      customInput.addEventListener('keydown', e => {
        if (e.key === 'ArrowUp')   { e.preventDefault(); setQty(currentQty + 1); }
        if (e.key === 'ArrowDown') { e.preventDefault(); setQty(currentQty - 1); }
        if (e.key === 'Enter')     { customInput.blur(); }
      });
    }

    // === Open / close popup ===
    function openPopup()  {
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
    function closePopup() {
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
    document.querySelectorAll('.gbpo-popup-button').forEach(b => {
      b.addEventListener('click', e => { e.preventDefault(); openPopup(); });
    });
    if (closeBtn) closeBtn.addEventListener('click', closePopup);
    if (maskEl)   maskEl.addEventListener('click',   closePopup);
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) closePopup();
    });

    // === CTA — Zakeke redirect (preserves original behaviour) ===
    // Bound to BOTH the popup button (.gbpo-button) AND the main-PDP secondary
    // button (.pi-cta-button), exactly like the original popupTreateApp.
    function doCheckout(e) {
      if (e) e.preventDefault();
      const variantPicker = document.querySelector('variant-picker');
      let variantId = '';
      if (variantPicker && variantPicker.getAttribute('form')) {
        const formEl = document.forms[variantPicker.getAttribute('form')];
        if (formEl && formEl.id) variantId = formEl.id.value || '';
      }
      const baseUrl = (ctaBtn && ctaBtn.dataset.redirectUrl)
        || 'https://tassenexpress.store/apps/zakeke?locale=de';
      let target;
      try { target = new URL(baseUrl); }
      catch (err) { window.location.href = baseUrl; return; }
      if (variantId) target.searchParams.set('id', variantId);
      target.searchParams.set('quantity', currentQty);
      window.location.href = target.toString();
    }
    if (ctaBtn) ctaBtn.addEventListener('click', doCheckout);
    const piCta = document.querySelector('.pi-cta-button');
    if (piCta) piCta.addEventListener('click', doCheckout);

    // === Init ===
    setQty(TIERS[0].qty);
    lastTierIdx = 0;
  }
})();
